import { computed, reactive } from 'vue'
import { useRoute } from 'vue-router'

export type DefaultPageSize = 'off' | 'low' | 'medium'
export type EffectivePageSize = 'low' | 'medium' | 'fullpage'

const overrides = reactive<Record<string, 'fullpage'>>({})

export function usePageSize() {
  const route = useRoute()

  const defaultSize = computed<DefaultPageSize>(() => {
    const explicit = route.meta.pageSize as DefaultPageSize | undefined
    if (explicit) return explicit
    const layout = route.meta.layout as string | undefined
    if (layout === 'fullscreen' || layout === 'modal') return 'off'
    if (layout === 'full') return 'off'
    if (layout === 'float-big') return 'medium'
    // layout === 'hide' has no sidebar overlay but should still show the map.
    return 'low'
  })

  const routeKey = computed(() => (route.name ? String(route.name) : null))

  const effectiveSize = computed<EffectivePageSize>(() => {
    if (defaultSize.value === 'off') return 'fullpage'
    if (routeKey.value && overrides[routeKey.value] === 'fullpage') return 'fullpage'
    return defaultSize.value
  })

  const canToggle = computed(() => defaultSize.value !== 'off')
  const isFullpage = computed(() => effectiveSize.value === 'fullpage')

  const toggle = () => {
    if (!canToggle.value || !routeKey.value) return
    if (overrides[routeKey.value] === 'fullpage') {
      delete overrides[routeKey.value]
    } else {
      overrides[routeKey.value] = 'fullpage'
    }
  }

  return { defaultSize, effectiveSize, canToggle, isFullpage, toggle }
}
